"use client";

import { useMemo, useState } from "react";
import {
  useQueryApps,
  useQueryImplementationsMeta,
  useQueryPrivateImplementationsMeta,
} from "@zapier/client";
import { ServiceIcon } from "@zapier/design-system";
import { FilterSelectMenu, type FilterMenuItem } from "../../FilterSelectMenu";
import { useNamespacedTranslations } from "../useNamespacedTranslations";
import type { MultiFilterOperator } from "../../../../hooks/useRouterParams";

interface Props {
  namespace: string;
  selectedIds: string[];
  operator: MultiFilterOperator;
  onOperatorChange: (operator: MultiFilterOperator) => void;
  onToggle: (id: string, pendingOperator?: MultiFilterOperator) => void;
  /** Operators offered in the match-type picker. Defaults to `["any", "all"]`.
   * Callers whose apps can only ever have one value per record (e.g.
   * Connections' `selectedApi`) should pass `["any"]` — "is all of" would be
   * indistinguishable from "is any of" there, so the picker is hidden. */
  availableOperators?: MultiFilterOperator[];
}

const stripVersion = (id: string): string => id.replace(/@[\w.-]+$/, "");
const stripVersionFromName = (name: string): string =>
  name.replace(/\s*\(\d[\w.-]*\)\s*$/, "").trim();

const visibilityFromIsPublic = (
  isPublic: boolean | undefined,
): "public" | "private" | undefined => {
  if (isPublic === true) return "public";
  if (isPublic === false) return "private";
  return undefined;
};

const toMenuItem = (
  app: {
    id?: string | null;
    name?: string | null;
    image?: string | null;
    visibility?: string | null;
  },
  appVisibilityMap: Map<string, string>,
): FilterMenuItem | undefined => {
  if (!app.id) return undefined;
  // Public apps collapse versioned IDs to versionless so the chip behaves
  // as "all versions of this app." Private apps keep their full ID —
  // distinct versions of a private integration are distinct logical apps
  // and must not collapse to one URL/chip entry.
  const visibility = app.visibility ?? appVisibilityMap.get(app.id);
  const id = visibility === "public" ? stripVersion(app.id) : app.id;
  const displayName = stripVersionFromName(app.name || id);
  return {
    id,
    name: displayName,
    renderIcon: () => (
      <ServiceIcon
        serviceName={displayName}
        size="small"
        src={app.image ?? ""}
      />
    ),
  };
};

const toPopularMenuItem = (app: {
  currentImplementationId?: string | null;
  name?: string | null;
  image?: string | null;
  images?: { url32x32?: string | null; url32X32?: string | null } | null;
}): FilterMenuItem | undefined => {
  const versionedId = app.currentImplementationId;
  if (!versionedId) return undefined;
  const id = stripVersion(versionedId);
  const displayName = stripVersionFromName(app.name || id);
  const image = app.image || app.images?.url32X32 || app.images?.url32x32 || "";
  return {
    id,
    name: displayName,
    renderIcon: () => (
      <ServiceIcon serviceName={displayName} size="small" src={image} />
    ),
  };
};

// `none` omitted: the monolith has no negative app filter.
const APP_OPERATORS: MultiFilterOperator[] = ["any", "all"];

export function AppFilterMenu({
  namespace,
  selectedIds,
  operator,
  onOperatorChange,
  onToggle,
  availableOperators = APP_OPERATORS,
}: Props) {
  const t = useNamespacedTranslations(namespace);
  const [search, setSearch] = useState("");
  const hasSearch = search.trim().length > 0;

  // Editor "Most popular" is GET /api/v4/apps with no title_search — same source.
  const {
    apps: popularApps,
    isLoading: isLoadingPopular,
    isFetching: isFetchingPopular,
  } = useQueryApps({ enabled: !hasSearch });

  const {
    implementationsMeta: searchedApps,
    isLoading: isLoadingSearch,
    isFetching: isFetchingSearch,
  } = useQueryImplementationsMeta({ term: search }, { enabled: hasSearch });

  // Hydrate selected apps that the current typeahead query doesn't surface.
  const { implementationsMeta: hydratedApps } =
    useQueryPrivateImplementationsMeta({
      selectedApis: selectedIds,
    });

  // Visibility map keyed on both the versioned ID returned by the API and
  // the versionless form. Public apps may already be stored as versionless
  // in the URL (because we collapse them — see `toMenuItem` below), so we
  // need to find the visibility either way.
  const appVisibilityMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const app of searchedApps) {
      if (app.id && app.visibility) {
        map.set(app.id, app.visibility);
        const versionless = stripVersion(app.id);
        if (versionless !== app.id) map.set(versionless, app.visibility);
      }
    }
    return map;
  }, [searchedApps]);

  const items = useMemo(() => {
    const byId = new Map<string, FilterMenuItem>();
    const ordered: string[] = [];
    const add = (item: FilterMenuItem | undefined) => {
      if (!item || byId.has(item.id)) return;
      byId.set(item.id, item);
      ordered.push(item.id);
    };

    if (hasSearch) {
      // While searching, show only matches — hydrated selections would muddle the results.
      for (const app of searchedApps) add(toMenuItem(app, appVisibilityMap));
      // Public apps first, others (including unknown-visibility) after —
      // matches the legacy `AppFilter` UX and the AC's QA criterion.
      return ordered
        .map((key) => byId.get(key)!)
        .sort((a, b) => {
          const aPublic = appVisibilityMap.get(a.id) === "public";
          const bPublic = appVisibilityMap.get(b.id) === "public";
          if (aPublic && !bPublic) return -1;
          if (!aPublic && bPublic) return 1;
          return 0;
        });
    }

    // Preserve /api/v4/apps order (editor Most popular ranking).
    for (const app of popularApps) add(toPopularMenuItem(app));
    for (const app of hydratedApps) {
      // Private meta exposes `isPublic`, not `visibility` — map it so public
      // selected apps collapse to versionless IDs and dedupe against popular.
      add(
        toMenuItem(
          {
            id: app.id ?? undefined,
            name: app.name ?? undefined,
            image: app.images?.url32X32 ?? undefined,
            visibility: visibilityFromIsPublic(app.isPublic),
          },
          appVisibilityMap,
        ),
      );
    }
    return ordered.map((key) => byId.get(key)!);
  }, [hasSearch, popularApps, searchedApps, hydratedApps, appVisibilityMap]);

  return (
    <FilterSelectMenu
      label={t("app.label")}
      selectedIds={selectedIds}
      operator={operator}
      availableOperators={availableOperators}
      operatorLabels={{
        any: t("app.operator-any"),
        all: t("app.operator-all"),
      }}
      items={items}
      search={search}
      isLoading={hasSearch ? isLoadingSearch : isLoadingPopular}
      isFetching={hasSearch ? isFetchingSearch : isFetchingPopular}
      searchPlaceholder={t("app.search-placeholder")}
      noResultsText={t("app.no-results")}
      onOperatorChange={onOperatorChange}
      onToggle={onToggle}
      onSearchChange={setSearch}
    />
  );
}
